/**
 * Canvas intent vocabulary — shared with backend (mirrored in
 * backend/app/schemas/canvas_intent.py). Algorithms declare semantic
 * meaning; the frontend mapper turns that into Cytoscape styling.
 *
 * Phase 0 of CANVAS_INTENT_MIGRATION_PLAN.md — type stubs only, no
 * consumers yet. Existing TARGET_ROOT/CRITICAL_ENDPOINT/HEAVY_CONTEXT/
 * AMBIENT_NOISE/STANDARD/PRIMARY_TRACE/SECONDARY_TRACE/LOGICAL_LINK
 * remain wired to applyCanvasIntent and the legacy class system. New
 * additions (EVIDENCE_ANCHOR, EVIDENCE_LINK, Confidence, Emphasis)
 * become live in Phase 1+.
 */

export const CanvasNodeIntent = {
  TARGET_ROOT: 'target-root',
  CRITICAL_ENDPOINT: 'critical-endpoint',
  EVIDENCE_ANCHOR: 'evidence-anchor',
  HEAVY_CONTEXT: 'heavy-context',
  AMBIENT_NOISE: 'ambient-noise',
  STANDARD: 'standard',
  /** @deprecated Phase 3 will remove. Danger is an attribute of the destination, not an intent of the path. Use CRITICAL_ENDPOINT + emphasis instead. */
  DANGER: 'danger',
} as const;
export type CanvasNodeIntent = typeof CanvasNodeIntent[keyof typeof CanvasNodeIntent];

export const CanvasEdgeIntent = {
  PRIMARY_TRACE: 'primary-trace',
  SECONDARY_TRACE: 'secondary-trace',
  EVIDENCE_LINK: 'evidence-link',
  LOGICAL_LINK: 'logical-link',
  AMBIENT_NOISE: 'ambient-noise',
  STANDARD: 'standard',
  /** @deprecated Phase 3 will remove. See CanvasNodeIntent.DANGER note. */
  DANGER_TRACE: 'danger-trace',
} as const;
export type CanvasEdgeIntent = typeof CanvasEdgeIntent[keyof typeof CanvasEdgeIntent];

/**
 * Confidence on edges. Proven = on-chain hash or subpoena evidence.
 * Inferred = pattern-matched, common-attribution, behavioural cluster.
 * Drives solid (proven) vs dashed (inferred) stroke in Phase 2.
 */
export const Confidence = {
  PROVEN: 'proven',
  INFERRED: 'inferred',
} as const;
export type Confidence = typeof Confidence[keyof typeof Confidence];

/**
 * Orthogonal display state — composes with intent. A CRITICAL_ENDPOINT
 * can also be flagged AND selected. Emphasis captures transient or
 * user-driven state; intent captures the algorithm's enduring claim.
 */
export const Emphasis = {
  NONE: 'none',
  FLAGGED: 'flagged',
  SELECTED: 'selected',
  HOVERED: 'hovered',
  DISCOVERING: 'discovering',
} as const;
export type Emphasis = typeof Emphasis[keyof typeof Emphasis];
